import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MoonStar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/staffAttendance/Badges';
import { CollarSelect, SectionSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { FixCheckoutDialog } from '@/components/staffAttendance/FixCheckoutDialog';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, fmtTime, todayIST } from '@/services/staffAttendanceApi';

const monthStart = () => `${todayIST().slice(0, 8)}01`;

const Row = ({ r, onFix }) => (
  <TableRow data-testid={`missed-row-${r.missed_id}`}>
    <TableCell className="font-mono text-sm">{r.date}</TableCell>
    <TableCell><div className="font-medium">{r.staff_name}</div><div className="text-[11px] text-gray-500">{r.staff_id} · {r.section}</div></TableCell>
    <TableCell><div className="font-mono">{fmtTime(r.in_time)}</div><div className="flex items-center gap-1 text-[11px] text-gray-500">Session {r.session_no}<StatusBadge status={r.in_status} /></div></TableCell>
    <TableCell data-testid={`missed-status-${r.missed_id}`}>
      {r.status === 'open'
        ? <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-bold">Open</span>
        : <div><span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs font-bold">Fixed</span>
            <div className="text-[11px] text-gray-600 mt-0.5">Out {fmtTime(r.fixed_out_time)} · {r.fixed_by_name}</div>
            <div className="text-[11px] text-gray-500 max-w-[220px] truncate" title={r.fixed_reason}>{r.fixed_reason}</div></div>}
    </TableCell>
    <TableCell className="text-right">
      <Button size="sm" variant={r.status === 'open' ? 'default' : 'outline'} disabled={r.in_status === 'rejected'} onClick={() => onFix(r)}
        className={r.status === 'open' ? 'bg-amber-600 hover:bg-amber-700' : ''} data-testid={`missed-fix-btn-${r.missed_id}`}>
        {r.status === 'open' ? 'Enter check-out' : 'Change'}
      </Button>
    </TableCell>
  </TableRow>
);

/** HR tab — sessions still open when the attendance day closed (default 4 AM IST). */
export const MissedCheckouts = ({ sections, search = '' }) => {
  const [status, setStatus] = useState('open');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIST());
  const [section, setSection] = useState('');
  const [collar, setCollar] = useState('');
  const [data, setData] = useState(null);
  const [fixing, setFixing] = useState(null);

  const load = useCallback(() => {
    setData(null);
    staffAttApi.missedCheckouts({ status, date_from: from || undefined, date_to: to || undefined, section: section || undefined, collar: collar || undefined })
      .then(setData).catch(() => setData({ rows: [], open: 0 }));
  }, [status, from, to, section, collar]);
  useEffect(() => { load(); }, [load]);
  const rows = (data?.rows || []).filter((r) => matchStaff(search, r.staff_name, r.staff_id));

  return (
    <div className="space-y-4" data-testid="missed-checkouts">
      <div className="flex items-start gap-3 rounded-xl bg-[#1A1F2E] text-white p-4">
        <MoonStar className="w-6 h-6 text-amber-300 mt-0.5" />
        <div><div className="font-semibold">Missed Check-outs <span className="ml-2 rounded-full bg-amber-500 px-2 text-xs font-bold" data-testid="missed-open-count">{data?.open ?? '…'} open</span></div>
          <p className="text-sm text-slate-300">When the attendance day closes (Settings → "Attendance day closes at", default 4 AM) every session still checked in lands here and supervisors get a Messenger note. Enter the real check-out time with a reason — it is saved as approved and the hours are added.</p></div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[130px] bg-white" data-testid="missed-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="fixed">Fixed</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px] bg-white" data-testid="missed-from-input" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px] bg-white" data-testid="missed-to-input" />
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="missed-section-filter" />
        <CollarSelect value={collar} onChange={setCollar} testId="missed-collar-filter" />
      </div>
      {data?.collar_split && <CollarTiles split={data.collar_split} value={collar} onPick={setCollar} metric="Missed check-outs" testId="missed-collar-tiles" />}
      {data && data.rows.length > 0 && <SearchNote q={search} shown={rows.length} total={data.rows.length} testId="missed-search-note" />}
      <div className="rounded-xl border bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Staff</TableHead><TableHead>Checked in</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => <Row key={r.missed_id} r={r} onFix={setFixing} />)}
            {!data && <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-amber-600" /></TableCell></TableRow>}
            {data && data.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8" data-testid="missed-empty">No missed check-outs for this filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {fixing && <FixCheckoutDialog row={fixing} onClose={() => setFixing(null)} onSaved={() => { setFixing(null); load(); }} />}
    </div>
  );
};

export default MissedCheckouts;
